import { computed, DestroyRef, inject, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Subscription } from 'rxjs';
import {
  ChatAttachment,
  ChatSession,
  ChatSessionMessage,
  Message,
  ConversationEvent,
} from './chat.model';
import { ChatService } from './chat.service';
import { ChatStore } from './chat.store';
import { sortSessionsByModified } from './chat.util';
import { CapabilityStore } from '../services/capability.store';
import { PaginationStore } from '../utils/pagination-store';

/** Number of conversations requested by each history page. */
const SESSION_PAGE_SIZE = 10;

/** Manages the selected conversation, saved history, and active response stream. */
@Injectable()
export class ChatConversationService {
  /** Services. */
  private readonly chatService = inject(ChatService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly store = inject(ChatStore);

  /** State. */
  readonly experts = this.store.experts;
  readonly selectedExpert = this.store.selectedExpert;
  readonly sessionHistory = new PaginationStore<ChatSession>(SESSION_PAGE_SIZE, pagination =>
    firstValueFrom(this.chatService.listSessions(pagination.page, pagination.perPage))
  );

  /** The loaded sessions, most recently modified first. */
  readonly sessions = computed(() => [...this.sessionHistory.items()].sort(sortSessionsByModified));
  readonly activeSession = this.store.activeSession;
  readonly messages = this.store.messages;
  readonly files = this.store.files;
  readonly currentAgentMsg = this.store.currentAgentMsg;
  readonly error = this.store.error;
  readonly loading = this.store.loading;
  readonly creating = this.store.creating;
  readonly isRunPending = this.store.isRunPending;
  readonly cancelling = this.store.cancelling;
  private socketSubscription?: Subscription;
  private streamSubscription?: Subscription;
  private creatingSession: Promise<ChatSession> | undefined;
  private selectionVersion = 0;
  private replaying = false;
  private bufferedEvents: ConversationEvent[] = [];
  private readonly seenEvents = new Set<string>();
  private readonly completedRuns = new Set<string>();
  private readonly runAttempts = new Map<string, number>();
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private connectedExpertId: string | undefined;

  /** Whether no conversation is currently selected. */
  readonly isOverview = this.store.isOverview;

  /** Whether the composer can accept a prompt. */
  readonly canSend = computed(
    () =>
      !!this.selectedExpert() &&
      !this.loading() &&
      !this.creating() &&
      !this.isRunPending() &&
      !this.chatService.connectionError() &&
      (!this.activeSession() || this.chatService.isConnected())
  );

  /** Loads chat data and closes connections with the owning component. */
  constructor() {
    void this.initialize();
    this.destroyRef.onDestroy(() => this.closeConnections());
  }

  /** Loads accessible experts and existing sessions. */
  private async initialize(): Promise<void> {
    try {
      const experts = await firstValueFrom(this.chatService.listExperts());
      this.store.setExperts(experts);
      await this.sessionHistory.initialLoad;
      await this.restoreSelectedSession();
    } catch (error) {
      console.error('Could not load chat', error);
      try {
        const health = await firstValueFrom(this.chatService.getHealth());
        if (health.status === 'degraded') {
          this.error.set('Chat service is degraded. Please try again later.');
        } else if (health.status === 'offline') {
          this.error.set('Chat service is unavailable. Please try again later.');
        } else {
          this.error.set('Could not load chats. Please try again later.');
        }
      } catch {
        this.error.set('Chat service is unavailable. Please try again later.');
      }
    } finally {
      this.loading.set(false);
    }
  }

  /** Restores a saved selection only if it belongs to an accessible session. */
  private async restoreSelectedSession(): Promise<void> {
    const sessionId = this.store.selectedSessionId();
    if (!sessionId) return;
    let session = this.sessions().find(item => item.id === sessionId);
    if (!session) {
      try {
        session = await firstValueFrom(this.chatService.getSession(sessionId));
      } catch (error) {
        if (!(error instanceof HttpErrorResponse) || ![403, 404].includes(error.status)) {
          throw error;
        }
        this.store.forgetSession(sessionId);
        return;
      }
    }
    await this.openSession(session);
  }

  /** Chooses the expert that will answer the next message. */
  selectExpert(expertId: string): void {
    this.store.selectExpert(expertId);
  }

  /** Loads and connects a saved conversation. */
  async openSession(session: ChatSession): Promise<void> {
    const version = ++this.selectionVersion;
    this.closeConnections();
    this.store.openSession(session);
    this.seenEvents.clear();
    this.completedRuns.clear();
    this.runAttempts.clear();
    this.loading.set(true);
    try {
      const history = await this.loadAllMessages(session.id);
      if (version !== this.selectionVersion) return;
      this.messages.set(history);
      for (const message of history) {
        if (message.sender === 'agent' && message.runId) this.completedRuns.add(message.runId);
      }
      await this.connectSavedSession(session, history, version);
    } catch (error) {
      if (version !== this.selectionVersion) return;
      console.error('Could not open chat', error);
      this.scheduleReconnect(session, version);
    } finally {
      if (version === this.selectionVersion) this.loading.set(false);
    }
  }

  /** Opens live delivery and replays events missed since the saved cursor. */
  private async connectSavedSession(
    session: ChatSession,
    history: Message[],
    version: number
  ): Promise<void> {
    const responsePending = history.at(-1)?.sender === 'user';
    this.isRunPending.set(
      responsePending && (session.status === 'streaming' || session.status === 'in_progress')
    );
    this.replaying = true;
    try {
      await this.connect(session, version);
      if (version === this.selectionVersion) {
        const after = responsePending ? undefined : this.store.savedCursor(session.id);
        await this.replayEvents(session, version, after);
      }
    } catch (error) {
      if (version === this.selectionVersion) this.replaying = false;
      throw error;
    }
  }

  /** Fetches all message pages for a session. */
  private async loadAllMessages(sessionId: string): Promise<Message[]> {
    const messages: Message[] = [];
    const [files, firstPage] = await Promise.all([
      firstValueFrom(this.chatService.listSessionFiles(sessionId)),
      firstValueFrom(this.chatService.getSession(sessionId, 1)),
    ]);
    if (this.activeSession()?.id === sessionId) this.files.set(files);
    let page = 1;
    let session = firstPage;
    while (true) {
      const visibleMessages = (session.messages ?? []).filter(value => !value.isDeleted);
      messages.push(...visibleMessages.map(value => this.toMessage(value)));
      if (!session.messagesPagination?.hasNext) break;
      page++;
      session = await firstValueFrom(this.chatService.getSession(sessionId, page));
    }
    return messages.reverse();
  }

  /** Converts a persisted API message into a display message. */
  private toMessage(value: ChatSessionMessage): Message {
    const attachments = (value.attachments ?? []).map(file => ({
      id: file.id,
      sessionId: file.sessionId,
      name: file.originalName,
      mimeType: file.mimeType,
    }));
    return {
      id: value.id,
      ...(value.agentRunId ? { runId: value.agentRunId } : {}),
      sender: value.senderType === 'USER' ? 'user' : 'agent',
      text: value.content,
      createdAt: value.createdAt,
      attachments,
    };
  }

  /** Opens the interactive WebSocket for a session. */
  private async connect(session: ChatSession, version = this.selectionVersion): Promise<void> {
    const expertId = this.selectedExpert()?.id ?? session.expertId;
    this.socketSubscription?.unsubscribe();
    this.connectedExpertId = undefined;
    const stream = await this.chatService.connectToSession(session.id, expertId);
    if (version !== this.selectionVersion) return;
    this.connectedExpertId = expertId;
    this.socketSubscription = stream.subscribe({
      next: event => this.receiveEvent(event),
      error: error => {
        if (version !== this.selectionVersion) return;
        console.error('Chat connection failed', error);
        this.scheduleReconnect(session, version);
      },
      complete: () => {
        if (version === this.selectionVersion) this.scheduleReconnect(session, version);
      },
    });
  }

  /** Binds the live socket to the selected expert before the next prompt. */
  private async connectSelectedExpert(session: ChatSession): Promise<void> {
    const version = this.selectionVersion;
    while (this.connectedExpertId !== this.selectedExpert()?.id) {
      this.replaying = true;
      try {
        await this.connect(session, version);
        await this.replayEvents(session, version, this.store.savedCursor(session.id));
      } catch (error) {
        if (version === this.selectionVersion) {
          this.replaying = false;
          this.scheduleReconnect(session, version);
        }
        throw error;
      }
      if (version !== this.selectionVersion) throw new Error('Chat selection changed');
    }
  }

  /** Merges finite replay with live events, applying each event once. */
  private async replayEvents(
    session: ChatSession,
    version: number,
    after: string | undefined
  ): Promise<void> {
    this.replaying = true;
    try {
      const stream = await this.chatService.connectToSessionStream(session.id, after);
      if (version !== this.selectionVersion) return;
      await new Promise<void>((resolve, reject) => {
        this.streamSubscription = stream.subscribe({
          next: event => this.bufferedEvents.push(event),
          error: reject,
          complete: resolve,
        });
      });
    } finally {
      if (version === this.selectionVersion) {
        this.replaying = false;
        const events = this.bufferedEvents.sort((a, b) => {
          if (a.cursorId < b.cursorId) return -1;
          if (a.cursorId > b.cursorId) return 1;
          return 0;
        });
        this.bufferedEvents = [];
        const latestRunId = events.at(-1)?.runId;
        for (const event of events) {
          if (event.runId !== latestRunId && this.isTerminalEvent(event)) {
            this.completedRuns.add(event.runId);
          }
        }
        for (const event of events) this.applyEvent(event);
      }
    }
  }

  /** Identifies the final event in an agent run. */
  private isTerminalEvent(event: ConversationEvent): boolean {
    return ['complete_run', 'run_failed', 'run_cancelled'].includes(event.type);
  }

  /** Holds live events until replay establishes their order. */
  private receiveEvent(event: ConversationEvent): void {
    if (this.replaying) this.bufferedEvents.push(event);
    else this.applyEvent(event);
  }

  /** Applies a stored event once and saves its replay position. */
  private applyEvent(event: ConversationEvent): void {
    if (event.sessionId !== this.activeSession()?.id) return;
    const identity = event.eventId ?? event.cursorId;
    if (this.seenEvents.has(identity)) return;
    this.seenEvents.add(identity);
    const activeTerminal = this.isRunPending() && this.isTerminalEvent(event);
    if (!this.completedRuns.has(event.runId) || activeTerminal) this.handleAgentEvent(event);
    const cursor = this.store.savedCursor(event.sessionId);
    if (!cursor || event.cursorId > cursor) this.store.saveCursor(event.sessionId, event.cursorId);
  }

  /** Applies run progress to the provisional response. */
  private handleAgentEvent(event: ConversationEvent): void {
    const wasWaitingForTool = this.currentAgentMsg()?.waitingForTool === true;
    const previousAttempt = this.runAttempts.get(event.runId);
    if (previousAttempt !== undefined && event.attempt < previousAttempt) return;
    if (previousAttempt !== undefined && event.attempt > previousAttempt) {
      this.currentAgentMsg.set(null);
    }
    this.runAttempts.set(event.runId, event.attempt);
    if (event.type === 'start_run') this.isRunPending.set(true);
    if (!this.currentAgentMsg()) {
      this.currentAgentMsg.set({
        sender: 'agent',
        runId: event.runId,
        text: '',
        timestamp: new Date(),
      });
    }
    this.currentAgentMsg.update(message => this.applyAgentEvent(message, event));
    const isWaitingForTool = this.currentAgentMsg()?.waitingForTool === true;
    if (isWaitingForTool !== wasWaitingForTool) {
      const capabilityStore = this.injector.get(CapabilityStore);
      capabilityStore.refreshJobHistories();
    }
    if (event.type === 'persist_event') void this.refreshPersistedMessages(event.sessionId);
    if (!this.isTerminalEvent(event)) return;
    this.isRunPending.set(false);
    this.cancelling.set(false);
    // The exchange is persisted now, so the session's modification time changed and the history must re-sort.
    this.sessionHistory.refresh();
    const finalMessage = this.currentAgentMsg();
    const alreadyPersisted = this.messages().some(message => message.runId === event.runId);
    if (!alreadyPersisted && (finalMessage?.text || finalMessage?.status)) {
      this.messages.update(messages => [...messages, finalMessage]);
    }
    this.currentAgentMsg.set(null);
    if (event.type === 'complete_run') void this.refreshPersistedMessages(event.sessionId);
  }

  /** Updates the visible response for a stream event. */
  private applyAgentEvent(message: Message | null, event: ConversationEvent): Message | null {
    if (!message) return null;
    const updated = { ...message };
    updated.waitingForTool = event.type === 'tool_execution';
    if (event.type === 'thought') updated.thought = `${updated.thought ?? ''}${event.content}`;
    if (event.type === 'response_delta') updated.text += event.content;
    if (event.type === 'run_status') updated.status = event.status;
    if (event.type === 'retry_scheduled')
      updated.status = event.errorMessage ?? 'Retrying response…';
    if (event.type === 'run_failed') {
      updated.status = event.errorMessage ?? event.errorCode ?? 'Response failed.';
    }
    if (event.type === 'run_cancelled') updated.status = 'Response stopped.';
    return updated;
  }

  /** Replaces provisional content with the persisted conversation after finalization. */
  private async refreshPersistedMessages(sessionId: string): Promise<void> {
    const version = this.selectionVersion;
    try {
      const history = await this.loadAllMessages(sessionId);
      if (version !== this.selectionVersion) return;
      this.messages.set(history);
      for (const message of history) {
        if (message.sender === 'agent' && message.runId) this.completedRuns.add(message.runId);
      }
    } catch (error) {
      console.error('Could not refresh chat history', error);
    }
  }

  /** Reopens a dropped live socket with a new ticket and catches up by replay. */
  private scheduleReconnect(session: ChatSession, version: number): void {
    if (this.reconnectTimer || version !== this.selectionVersion) return;
    this.error.set('Chat connection lost. Reconnecting…');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      void this.reconnect(session, version);
    }, 1000);
  }

  /** Restores persisted messages before accepting another prompt. */
  private async reconnect(session: ChatSession, version: number): Promise<void> {
    if (this.replaying) {
      this.scheduleReconnect(session, version);
      return;
    }
    try {
      await this.refreshPersistedMessages(session.id);
      if (version !== this.selectionVersion) return;
      const current = await firstValueFrom(this.chatService.getSession(session.id));
      if (version !== this.selectionVersion) return;
      const lastMessage = this.messages().at(-1);
      const active = current.status === 'streaming' || current.status === 'in_progress';
      this.isRunPending.set(active && lastMessage?.sender === 'user');
      if (!this.isRunPending()) this.cancelling.set(false);
      this.socketSubscription?.unsubscribe();
      this.replaying = true;
      await this.connect(session, version);
      await this.replayEvents(session, version, this.store.savedCursor(session.id));
      if (version === this.selectionVersion) this.error.set('');
    } catch (error) {
      if (version !== this.selectionVersion) return;
      this.replaying = false;
      console.error('Could not reconnect chat', error);
      this.scheduleReconnect(session, version);
    }
  }

  /** Clears the selected conversation while keeping the chosen expert. */
  newChat(): void {
    ++this.selectionVersion;
    this.closeConnections();
    this.store.clearSession();
  }

  /** Creates and connects a session when a prompt or file needs one. */
  async ensureSession(): Promise<ChatSession> {
    if (this.creatingSession) return this.creatingSession;
    const existing = this.activeSession();
    if (existing) return existing;
    const expert = this.selectedExpert();
    if (!expert) throw new Error('No expert selected');

    const version = this.selectionVersion;
    this.creating.set(true);
    const creation = (async () => {
      const created = await firstValueFrom(this.chatService.createSession(expert.id));
      if (version !== this.selectionVersion) throw new Error('Chat selection changed');
      this.store.startSession(created);
      this.sessionHistory.refresh();
      await this.connect(created, version);
      return created;
    })();
    this.creatingSession = creation;
    try {
      return await creation;
    } finally {
      this.creatingSession = undefined;
      this.creating.set(false);
    }
  }

  /** Sends a prompt over the selected session's socket. */
  async sendMessage(text: string, attachments: ChatAttachment[] = []): Promise<boolean> {
    const prompt = text.trim();
    if ((!prompt && !attachments.length) || !this.canSend()) return false;
    try {
      const session = await this.ensureSession();
      await this.connectSelectedExpert(session);
      if (this.isRunPending()) return false;
      this.chatService.sendMessage(
        prompt,
        attachments.map(file => file.id)
      );
      this.isRunPending.set(true);
      this.messages.update(messages => [
        ...messages,
        { sender: 'user', text: prompt, attachments, createdAt: new Date().toISOString() },
      ]);
      return true;
    } catch (error) {
      console.error('Could not send chat message', error);
      this.error.set('Could not send your message.');
      return false;
    }
  }

  /** Requests cancellation of the active response. */
  async cancelRun(): Promise<void> {
    const session = this.activeSession();
    if (!session || !this.isRunPending() || this.cancelling()) return;
    const version = this.selectionVersion;
    this.cancelling.set(true);
    let cancellationPending = false;
    try {
      const result = await firstValueFrom(this.chatService.cancelRun(session.id));
      if (version !== this.selectionVersion) return;
      cancellationPending = result.cancelled && result.status === 'running';
      if (result.status !== 'running') {
        this.closeConnections();
        const messages = await this.loadAllMessages(session.id);
        if (version !== this.selectionVersion) return;
        this.messages.set(messages);
        this.currentAgentMsg.set(null);
        this.isRunPending.set(false);
        void this.connectSavedSession(session, messages, version).catch(error => {
          if (version !== this.selectionVersion) return;
          console.error('Could not reconnect chat', error);
          this.error.set('Chat connection lost.');
        });
      }
    } catch (error) {
      if (version !== this.selectionVersion) return;
      if (error instanceof HttpErrorResponse && error.status === 404) {
        await this.refreshPersistedMessages(session.id);
        if (version !== this.selectionVersion) return;
        this.isRunPending.set(false);
        return;
      }
      console.error('Could not stop response', error);
      this.error.set('Could not stop this response.');
    } finally {
      if (version === this.selectionVersion && !cancellationPending) this.cancelling.set(false);
    }
  }

  /** Deletes a conversation through the API. */
  async deleteSession(session: ChatSession): Promise<void> {
    await firstValueFrom(this.chatService.deleteSession(session.id));
    this.store.forgetSession(session.id);
    this.sessionHistory.refresh();
    if (this.activeSession()?.id === session.id) this.newChat();
  }

  /** Closes subscriptions and sockets before changing sessions. */
  private closeConnections(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.replaying = false;
    this.bufferedEvents = [];
    this.connectedExpertId = undefined;
    this.socketSubscription?.unsubscribe();
    this.streamSubscription?.unsubscribe();
    this.chatService.disconnect();
  }
}
